import { PDFDocument, PDFPage, rgb } from 'pdf-lib';
import fontkit from '@pdf-lib/fontkit';
import {
  BatteryInfoData,
  BatteryMeasurements,
} from '../../api/service/BatteryInterfaces';
import PretendardFont from '../../assets/fonts/PretendardVariable.ttf';
import PDFleaf from '../../assets/images/PDFleaf.png';
import PDFlogo from '../../assets/images/PDFlogo.png';
import SellerBatteryService from '../../api/service/seller/SellerBatteryService';

const generateDiagnosisTab3PDF = async (
  batteryInfo: BatteryInfoData,
  batteryId: string
) => {
  const categories = ['SENSOR_DATA', 'BATTERY_CELL_VOLTAGE', 'FAILURE_CODE'];

  // 데이터 로드 함수
  const fetchAllData = async (category: string) => {
    let page = 0;
    const allData: BatteryMeasurements[] = [];
    let hasMore = true;

    while (hasMore) {
      try {
        const response = await SellerBatteryService.getBatteryMeasurements(
          batteryId,
          page,
          10, // 한 페이지에 표시할 데이터 수
          category
        );

        const newData = response.data.content || [];
        allData.push(...newData);
        hasMore = newData.length > 0; // 데이터가 없으면 종료
        page += 1;
      } catch (error) {
        console.error(`Error fetching data for category ${category}:`, error);
        hasMore = false; // 에러 발생 시 루프 종료
      }
    }

    return allData;
  };

  // 카테고리별 데이터 가져오기
  const allCategoryData = await Promise.all(
    categories.map((category) => fetchAllData(category))
  );

  const pdfDoc = await PDFDocument.create();
  pdfDoc.registerFontkit(fontkit);

  const fontBytes = await fetch(PretendardFont).then((res) =>
    res.arrayBuffer()
  );
  const pretendardFont = await pdfDoc.embedFont(fontBytes);

  const loadImage = async (pdfDoc: PDFDocument, imagePath: string) => {
    const imageBytes = await fetch(imagePath).then((res) => res.arrayBuffer());
    const image = await pdfDoc.embedPng(imageBytes);
    return image;
  };

  // 이미지 로드
  const logoImage = await loadImage(pdfDoc, PDFlogo);
  const leafImage = await loadImage(pdfDoc, PDFleaf);

  const pageSize: [number, number] = [841.89, 595.28]; // A4 가로 크기
  let page: PDFPage = pdfDoc.addPage(pageSize);

  const { width, height } = page.getSize();
  const fontSize = 10;
  const titleFontSize = 16; // 타이틀 폰트 크기
  const margin = 10; // 좌우 여백
  let yPosition = height;

  // 타이틀 텍스트
  const titleHeight = 40; // 타이틀 영역 높이
  const title = '진단 데이터';
  const titleBgColor = rgb(0.137, 0.549, 0.392);
  const titleTextColor = rgb(1, 1, 1);

  // 타이틀 배경
  page.drawRectangle({
    x: 0,
    y: yPosition - titleHeight,
    width: width,
    height: titleHeight,
    color: titleBgColor,
  });

  // 이미지 삽입

  const logoDims = logoImage.scale(0.5);

  const logoX = margin;
  const logoY = yPosition - titleHeight + (titleHeight - logoDims.height) / 2;

  page.drawImage(logoImage, {
    x: logoX,
    y: logoY,
    width: logoDims.width,
    height: logoDims.height,
  });

  const titleX =
    width - margin - pretendardFont.widthOfTextAtSize(title, titleFontSize);
  const titleY = yPosition - titleHeight / 2 - titleFontSize / 2;

  page.drawText(title, {
    x: titleX,
    y: titleY,
    size: titleFontSize,
    font: pretendardFont,
    color: titleTextColor,
  });

  yPosition -= titleHeight + 10;

  const subTitleFontSize = 12; // 타이틀 폰트 크기
  const titleHeight2 = 20; // 타이틀 영역 높이
  const title2 = '기본정보'; // 타이틀 텍스트
  const titleTextColor2 = rgb(0, 0, 0);

  // 타이틀 배경
  page.drawRectangle({
    x: 0,
    y: yPosition - titleHeight2,
    width: width,
    height: titleHeight2,
    color: rgb(1, 1, 1), // 원하는 배경색
  });

  // 이미지 삽입
  const imageDims = leafImage.scale(0.5); // 이미지 크기 조정

  const imageX = margin; // 왼쪽 여백
  const imageY =
    yPosition - titleHeight2 + (titleHeight2 - imageDims.height) / 2; // 중앙 정렬

  page.drawImage(leafImage, {
    x: imageX,
    y: imageY,
    width: imageDims.width,
    height: imageDims.height,
  });

  // 텍스트 왼쪽 정렬
  const textStartX = imageX + imageDims.width + 10; // 이미지 오른쪽 + 여백
  const textStartY = yPosition - titleHeight2 / 2 - subTitleFontSize / 2;

  page.drawText(title2, {
    x: textStartX,
    y: textStartY,
    size: subTitleFontSize,
    font: pretendardFont,
    color: titleTextColor2,
  });

  yPosition -= titleHeight2 + 10; // Y 위치 업데이트

  // 공통 열 너비 계산 함수
  const calculateDynamicColumnWidths1 = (
    pageWidth: number,
    margin: number,
    headers: string[],
    tableData: string[], // 1차원 배열
    font: typeof pretendardFont,
    fontSize: number
  ): number[] => {
    const availableWidth = pageWidth - margin * 2;
    const maxWidths = headers.map((header, i) =>
      Math.max(
        font.widthOfTextAtSize(header, fontSize),
        font.widthOfTextAtSize(tableData[i] || '-', fontSize) // tableData를 인덱스로 접근
      )
    );

    const totalWidth = maxWidths.reduce((sum, w) => sum + w, 0);
    const scaleFactor = availableWidth / totalWidth;

    return maxWidths.map((w) => w * scaleFactor);
  };

  // 헤더 그리기 함수
  const drawHeaderRow = (
    headers: string[],
    y: number,
    headerTextColor: ReturnType<typeof rgb>,
    headerBgColor: ReturnType<typeof rgb>,
    columnXPositions: number[],
    columnWidths: number[]
  ) => {
    page.drawRectangle({
      x: margin,
      y: y - fontSize - 7,
      width: width - margin * 2,
      height: fontSize + 8,
      color: headerBgColor,
    });

    headers.forEach((header, i) => {
      const textWidth = pretendardFont.widthOfTextAtSize(header, fontSize);
      const columnCenter = columnXPositions[i] + columnWidths[i] / 2;
      const textStartX = columnCenter - textWidth / 2;

      page.drawText(header, {
        x: textStartX,
        y: y - fontSize - 2,
        size: fontSize,
        font: pretendardFont,
        color: headerTextColor,
      });
    });
  };

  // batteryDamageInfo 출력
  // Header를 한글로 지정
  const infoHeaders = [
    '제조사',
    '차종',
    '차량번호',
    '제품번호',
    'ROM ID',
    'QR CODE',
    '생산일',
    '측정일',
  ];

  // 데이터에 값을 배열로 매칭
  const infoData = [
    batteryInfo.manufacturer || '-',
    batteryInfo.model || '-',
    batteryInfo.carNo || '-',
    batteryInfo.productNo || '-',
    batteryInfo.romId || '-',
    batteryInfo.qrcode || '-',
    batteryInfo.productionDate || '-',
    batteryInfo.measurementDate || '-',
  ];

  const infoColumnWidths = calculateDynamicColumnWidths1(
    width,
    margin,
    infoHeaders,
    infoData,
    pretendardFont,
    fontSize
  );

  const infoColumnXPositions = infoColumnWidths.reduce(
    (acc, w) => [...acc, acc[acc.length - 1] + w],
    [margin]
  );

  drawHeaderRow(
    infoHeaders,
    yPosition,
    rgb(1, 1, 1),
    rgb(0.569, 0.776, 0.698),
    infoColumnXPositions,
    infoColumnWidths
  );

  yPosition -= fontSize + 12;

  infoData.forEach((text, i) => {
    const safeValue = text || '-'; // undefined 방지
    const textWidth = pretendardFont.widthOfTextAtSize(safeValue, fontSize);
    const columnCenter = infoColumnXPositions[i] + infoColumnWidths[i] / 2;
    const textStartX = columnCenter - textWidth / 2;

    // 페이지 하단 도달 시 새 페이지 추가
    if (yPosition < margin + fontSize + 10) {
      page = pdfDoc.addPage(pageSize);
      yPosition = height - margin - 20;
    }

    // 텍스트 렌더링
    page.drawText(safeValue, {
      x: textStartX,
      y: yPosition - fontSize - 2,
      size: fontSize,
      font: pretendardFont,
      color: rgb(0, 0, 0), // 검정색 텍스트
    });
  });

  // 행 간격 추가
  yPosition -= fontSize + 10;

  // 카테고리를 한글로 매핑
  const categoryTranslations: { [key: string]: string } = {
    SENSOR_DATA: '센서 데이터',
    BATTERY_CELL_VOLTAGE: '배터리 셀 전압',
    FAILURE_CODE: '고장 코드',
  };

  // 각 카테고리 데이터 출력
  categories.forEach((category, index) => {
    if (yPosition < 50) {
      page = pdfDoc.addPage(pageSize);
      yPosition = height - 50;
    }

    // 한글 카테고리 이름 변환
    const translatedCategory = categoryTranslations[category] || category;

    // 카테고리 타이틀 스타일 설정
    const subTitleFontSize = 12; // 폰트 크기
    const titleHeight2 = 20; // 타이틀 높이
    const titleTextColor2 = rgb(0, 0, 0); // 텍스트 색상
    const backgroundColor = rgb(1, 1, 1); // 배경색

    // 타이틀 배경 그리기
    page.drawRectangle({
      x: 0,
      y: yPosition - titleHeight2,
      width: width,
      height: titleHeight2,
      color: backgroundColor,
    });

    const imageDims = leafImage.scale(0.5); // 이미지 크기 조정

    const imageX = margin; // 이미지 X 위치
    const imageY =
      yPosition - titleHeight2 + (titleHeight2 - imageDims.height) / 2; // 이미지 Y 위치

    page.drawImage(leafImage, {
      x: imageX,
      y: imageY,
      width: imageDims.width,
      height: imageDims.height,
    });

    // 텍스트 출력 (왼쪽 정렬)
    const textStartX = imageX + imageDims.width + 10; // 이미지 오른쪽 + 여백
    const textStartY = yPosition - titleHeight2 / 2 - subTitleFontSize / 2;

    page.drawText(`${translatedCategory}`, {
      x: textStartX,
      y: textStartY,
      size: subTitleFontSize,
      font: pretendardFont,
      color: titleTextColor2,
    });

    yPosition -= titleHeight2 + 10; // Y 위치 업데이트

    const headers =
      category === 'FAILURE_CODE'
        ? ['NO', '상태', '고장코드', '고장코드명']
        : ['NO', '센서 이름', '측정 값', '단위'];

    const data = allCategoryData[index].map((item, i) => [
      (i + 1).toString(),
      item.codeStatus || item.sensorName || '-',
      item.codeValue || item.sensorResult || '-',
      item.codeName || item.sensorUnit || '-',
    ]);

    // 열 너비 동적 계산
    const columnWidths = calculateDynamicColumnWidths1(
      width,
      margin,
      headers,
      data.flat(),
      pretendardFont,
      fontSize
    );

    const columnXPositions = columnWidths.reduce(
      (acc, w) => [...acc, acc[acc.length - 1] + w],
      [margin]
    );

    // 헤더 출력
    drawHeaderRow(
      headers,
      yPosition,
      rgb(1, 1, 1),
      rgb(0.569, 0.776, 0.698),
      columnXPositions,
      columnWidths
    );

    yPosition -= fontSize + 12;

    // 데이터 출력
    data.forEach((row) => {
      if (yPosition < margin + fontSize + 10) {
        page = pdfDoc.addPage(pageSize);
        yPosition = height - margin - 20;

        // 새 페이지 헤더 출력
        drawHeaderRow(
          headers,
          yPosition,
          rgb(1, 1, 1),
          rgb(0.569, 0.776, 0.698),
          columnXPositions,
          columnWidths
        );

        yPosition -= fontSize + 12;
      }

      row.forEach((cell, i) => {
        const textWidth = pretendardFont.widthOfTextAtSize(
          cell || '-',
          fontSize
        );
        const columnCenter = columnXPositions[i] + columnWidths[i] / 2;
        const textStartX = columnCenter - textWidth / 2;

        page.drawText(cell || '-', {
          x: textStartX,
          y: yPosition - fontSize - 2,
          size: fontSize,
          font: pretendardFont,
          color: rgb(0, 0, 0),
        });
      });

      yPosition -= fontSize + 10;
    });
  });

  const pdfBytes = await pdfDoc.save();
  return pdfBytes;
};

export default generateDiagnosisTab3PDF;
